<template>
  <div class="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
    <!-- Background Effects -->
    <div class="fixed inset-0 pointer-events-none overflow-hidden">
      <div class="absolute top-20 left-20 w-72 h-72 bg-purple-300 dark:bg-purple-600 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-20 dark:opacity-10 animate-blob"></div>
      <div class="absolute top-40 right-20 w-72 h-72 bg-indigo-300 dark:bg-indigo-600 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-20 dark:opacity-10 animate-blob animation-delay-2000"></div>
      <div class="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-300 dark:bg-pink-600 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-20 dark:opacity-10 animate-blob animation-delay-4000"></div>
    </div>

    <!-- Navigation Bar -->
    <nav class="fixed top-0 w-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-md z-50 border-b border-gray-100 dark:border-gray-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo -->
          <router-link :to="{ name: 'welcome', params: { locale: currentLocale } }" class="flex items-center space-x-3">
            <img
              src="/img/logo.webp"
              alt="MailerX"
              class="h-10"
            />
          </router-link>

          <!-- Right side: Language Switcher and Back Link -->
          <div class="flex items-center space-x-4">
            <LanguageSwitcher :compact="true" position="right" />

            <router-link
              :to="{ name: 'welcome', params: { locale: currentLocale } }"
              class="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors flex items-center group"
            >
              <ArrowLeftIcon class="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              {{ $t('auth.back_to_home') }}
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <div class="relative z-10 flex min-h-screen items-center justify-center py-12 px-4 sm:px-6 lg:px-8 pt-24">
      <div class="w-full max-w-md">
        <!-- Auth Card -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-700">
          <router-view />
        </div>

        <!-- Footer Links -->
        <div class="mt-8 text-center space-y-4">
          <!-- Sign up / Sign in toggle -->
          <p v-if="isLoginPage" class="text-gray-600 dark:text-gray-400">
            {{ $t('auth.dont_have_account') }}
            <router-link
              :to="{ name: 'register', params: { locale: currentLocale } }"
              class="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors"
            >
              {{ $t('auth.sign_up') }}
            </router-link>
          </p>
          <p v-else-if="isRegisterPage" class="text-gray-600 dark:text-gray-400">
            {{ $t('auth.already_have_account') }}
            <router-link
              :to="{ name: 'login', params: { locale: currentLocale } }"
              class="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors"
            >
              {{ $t('auth.sign_in') }}
            </router-link>
          </p>

          <!-- Copyright -->
          <p class="text-sm text-gray-500 dark:text-gray-500">
            © {{ new Date().getFullYear() }} MailerX. {{ $t('welcome.footer.copyright', { year: '' }).replace('© ', '').replace('. ', '') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowLeftIcon } from '@heroicons/vue/24/outline'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'

const route = useRoute()
const currentLocale = computed(() => route.params.locale || 'en')

const isLoginPage = computed(() => route.name === 'login')
const isRegisterPage = computed(() => route.name === 'register')
</script>

<style scoped>
@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}
</style>
